<template>
  <div class="exam-analysis-container" v-loading="loading" element-loading-text="加载中...">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1>考试分析</h1>
      <div class="header-actions">
        <el-button type="primary" @click="exportReport" :loading="exportLoading">
          <i class="el-icon-download"></i> 导出报告
        </el-button>
        <el-button @click="refreshData" :loading="loading">
          <i class="el-icon-refresh"></i> 刷新数据
        </el-button>
      </div>
    </div>

    <!-- 考试选择 -->
    <div class="exam-selector">
      <el-form :inline="true" class="demo-form-inline">
        <el-form-item label="选择考试：">
          <el-select v-model="selectedExamId" placeholder="请选择考试" @change="onExamChange" style="width: 300px;">
            <el-option
              v-for="exam in examList"
              :key="exam.id"
              :label="exam.title"
              :value="exam.id">
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>

      <!-- 调试信息 -->
      <div style="margin-top: 10px; color: #909399; font-size: 12px;">
        已加载 {{ examList.length }} 个考试
        <span v-if="examList.length === 0" style="color: #F56C6C;">（暂无考试数据）</span>
      </div>
    </div>
    <!-- 提示信息 -->
    <div v-if="!selectedExamId && examList.length > 0" class="tip-container">
      <el-alert
        title="请选择一个考试进行分析"
        type="info"
        :closable="false"
        show-icon>
      </el-alert>
    </div>

    <!-- 主要内容区域 -->
    <div v-if="analysisData" class="analysis-content">
      <!-- 考试基本信息 -->
      <el-card class="exam-info-card">
        <div slot="header">
          <span>考试基本信息</span>
        </div>
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">考试名称</div>
              <div class="info-value">{{ analysisData.examTitle }}</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">参与人数</div>
              <div class="info-value">{{ analysisData.statistics.totalStudents }}人</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">完成人数</div>
              <div class="info-value">{{ analysisData.statistics.completedStudents }}人</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">平均分</div>
              <div class="info-value">{{ analysisData.statistics.avgScore }}分</div>
            </div>
          </el-col>
        </el-row>
        <el-row :gutter="20" style="margin-top: 20px;">
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">最高分</div>
              <div class="info-value">{{ analysisData.statistics.maxScore }}分</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">最低分</div>
              <div class="info-value">{{ analysisData.statistics.minScore }}分</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">及格率</div>
              <div class="info-value">{{ analysisData.statistics.passRate }}%</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">优秀率</div>
              <div class="info-value">{{ analysisData.statistics.excellentRate }}%</div>
            </div>
          </el-col>
        </el-row>

        <!-- 分数相关（来自记录页） -->
        <el-row :gutter="20" style="margin-top: 20px;">
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">试卷总分</div>
              <div class="info-value">{{ analysisData.statistics.totalScore || 0 }}分</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">我的得分</div>
              <div class="info-value">{{ myRecord && myRecord.userScore != null ? myRecord.userScore : '—' }}分</div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="info-item">
              <div class="info-label">我的用时</div>
              <div class="info-value">{{ myRecord && myRecord.userTime != null ? formatUserTime(myRecord.userTime) : '—' }}</div>
            </div>
          </el-col>
        </el-row>
      </el-card>

      <!-- 统计图表 -->
      <el-row :gutter="20" style="margin-top: 20px;">
        <el-col :span="12">
          <el-card>
            <div slot="header">
              <span>分数段分布</span>
            </div>
            <div ref="scoreChart" style="height: 300px;"></div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card>
            <div slot="header">
              <span>时间分布</span>
            </div>
            <div ref="timeChart" style="height: 300px;"></div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 题目分析 -->
      <el-card style="margin-top: 20px;">
        <div slot="header">
          <span>题目分析</span>
        </div>
        <el-table :data="analysisData.questionDetails" style="width: 100%">
          <el-table-column prop="questionId" label="题号" width="80"></el-table-column>
          <el-table-column prop="questionContent" label="题目内容" min-width="300">
            <template slot-scope="scope">
              <div class="question-content">{{ scope.row.questionContent }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="questionTypeName" label="题型" width="100"></el-table-column>
          <el-table-column prop="score" label="分值" width="80"></el-table-column>
          <el-table-column prop="correctRate" label="正确率" width="100">
            <template slot-scope="scope">
              <el-progress
                :percentage="scope.row.correctRate"
                :color="getProgressColor(scope.row.correctRate)">
              </el-progress>
            </template>
          </el-table-column>
          <el-table-column prop="difficultyLevelName" label="难度" width="100">
            <template slot-scope="scope">
              <el-tag :type="getDifficultyType(scope.row.difficultyLevel)">
                {{ scope.row.difficultyLevelName }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120">
            <template slot-scope="scope">
              <el-button type="text" @click="viewQuestionDetail(scope.row)">
                查看详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <!-- 学生答题详情 -->
      <el-card style="margin-top: 20px;">
        <div slot="header">
          <span>学生答题详情</span>
        </div>
        <el-table :data="analysisData.studentAnswers" style="width: 100%">
          <el-table-column prop="userRealName" label="学生姓名" width="120"></el-table-column>
          <el-table-column prop="userName" label="用户名" width="120"></el-table-column>
          <el-table-column prop="questionContent" label="题目内容" min-width="200">
            <template slot-scope="scope">
              <div class="question-content">{{ scope.row.questionContent }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="questionTypeName" label="题型" width="100"></el-table-column>
          <el-table-column prop="studentAnswer" label="学生答案" width="150">
            <template slot-scope="scope">
              <div class="answer-content">{{ scope.row.studentAnswer }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="correctAnswer" label="正确答案" width="150">
            <template slot-scope="scope">
              <div class="answer-content">{{ scope.row.correctAnswer }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="isRight" label="是否正确" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.isRight === 1 ? 'success' : 'danger'">
                {{ scope.row.isRight === 1 ? '正确' : '错误' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="score" label="得分" width="80"></el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- 题目详情对话框 -->
    <el-dialog title="题目详细分析" :visible.sync="questionDetailVisible" width="80%">
      <div v-if="selectedQuestion">
        <el-card>
          <div slot="header">
            <span>题目信息</span>
          </div>
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="detail-item">
                <label>题目内容：</label>
                <div>{{ selectedQuestion.questionContent }}</div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="detail-item">
                <label>正确答案：</label>
                <div>{{ selectedQuestion.correctAnswer }}</div>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="20" style="margin-top: 20px;">
            <el-col :span="12">
              <div class="detail-item">
                <label>题目解析：</label>
                <div>{{ selectedQuestion.analysis || '暂无解析' }}</div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="detail-item">
                <label>正确率：</label>
                <div>{{ selectedQuestion.correctRate }}%</div>
              </div>
            </el-col>
          </el-row>
        </el-card>

        <!-- 选项分析 -->
        <el-card style="margin-top: 20px;" v-if="selectedQuestion.options && selectedQuestion.options.length > 0">
          <div slot="header">
            <span>选项分析</span>
          </div>
          <el-table :data="selectedQuestion.options" style="width: 100%">
            <el-table-column prop="optionLabel" label="选项" width="80"></el-table-column>
            <el-table-column prop="optionContent" label="选项内容" min-width="200"></el-table-column>
            <el-table-column prop="selectedCount" label="选择人数" width="100"></el-table-column>
            <el-table-column prop="selectedRate" label="选择率" width="100">
              <template slot-scope="scope">
                {{ scope.row.selectedRate }}%
              </template>
            </el-table-column>
            <el-table-column prop="isCorrect" label="是否正确答案" width="120">
              <template slot-scope="scope">
                <el-tag :type="scope.row.isCorrect ? 'success' : 'info'">
                  {{ scope.row.isCorrect ? '是' : '否' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>

        <!-- 学生答题详情 -->
        <el-card style="margin-top: 20px;" v-if="selectedQuestion.studentAnswers && selectedQuestion.studentAnswers.length > 0">
          <div slot="header">
            <span>学生答题详情</span>
          </div>
          <el-table :data="selectedQuestion.studentAnswers" style="width: 100%">
            <el-table-column prop="userRealName" label="学生姓名" width="120"></el-table-column>
            <el-table-column prop="userName" label="用户名" width="120"></el-table-column>
            <el-table-column prop="studentAnswer" label="学生答案" width="150"></el-table-column>
            <el-table-column prop="isRight" label="是否正确" width="100">
              <template slot-scope="scope">
                <el-tag :type="scope.row.isRight === 1 ? 'success' : 'danger'">
                  {{ scope.row.isRight === 1 ? '正确' : '错误' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="score" label="得分" width="80"></el-table-column>
          </el-table>
        </el-card>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getExamDetailAnalysis, exportAnalysisReport } from '@/api/examAnalysis'
import { getGradeExamList } from '@/api/exam'
import * as echarts from 'echarts'

export default {
  name: 'ExamAnalysis',
  data() {
    return {
      loading: false,
      exportLoading: false,
      selectedExamId: null,
      examList: [],
      analysisData: null,
      myRecord: null,
      questionDetailVisible: false,
      selectedQuestion: null,
      scoreChart: null,
      timeChart: null
    }
  },
  mounted() {
    console.log('ExamAnalysis component mounted')
    this.loadExamList()
  },
  methods: {
    // 加载考试列表
    async loadExamList() {
      try {
        console.log('Loading exam list...')
        const response = await getGradeExamList({ pageNum: 1, pageSize: 100 })
        console.log('Exam list response:', response)
        // 修复：后端返回code为1表示成功，code为0表示失败
        if (response.code === 1) {
          // 检查数据结构，可能直接是data而不是data.records
          if (response.data && response.data.records) {
            this.examList = response.data.records
          } else if (response.data && Array.isArray(response.data)) {
            this.examList = response.data
          } else {
            this.examList = []
          }
          console.log('Exam list loaded:', this.examList)
          console.log('Exam list length:', this.examList.length)
        } else {
          console.error('加载考试列表失败:', response.msg)
          this.$message.error(response.msg || '加载考试列表失败')
        }
      } catch (error) {
        console.error('加载考试列表失败:', error)
        this.$message.error('加载考试列表失败')
      }
    },

    // 考试选择变化
    async onExamChange(examId) {
      if (examId) {
        await Promise.all([
          this.loadAnalysisData(examId),
          this.loadMyRecord(examId)
        ])
      }
    },

    // 加载分析数据
    async loadAnalysisData(examId) {
      this.loading = true
      try {
        const response = await getExamDetailAnalysis(examId)
        // 修复：后端返回code为1表示成功，code为0表示失败
        if (response.code === 1) {
          this.analysisData = response.data
          this.$nextTick(() => {
            this.initCharts()
          })
        } else {
          this.$message.error(response.msg || '获取分析数据失败')
        }
      } catch (error) {
        console.error('加载分析数据失败:', error)
        this.$message.error('加载分析数据失败')
      } finally {
        this.loading = false
      }
    },

    // 加载“我的成绩/用时”（来源记录接口）
    async loadMyRecord(examId) {
      try {
        // 直接复用记录分页接口，过滤当前试卷，取当前用户那条记录
        const { recordExamPaging } = await import('@/api/record')
        const res = await recordExamPaging({ pageNum: 1, pageSize: 200, examName: '', isASC: false })
        if (res && res.data && Array.isArray(res.data.records)) {
          const list = res.data.records
          const mine = list.find(item => item.id === examId)
          this.myRecord = mine || null
        } else {
          this.myRecord = null
        }
      } catch (e) {
        this.myRecord = null
        console.error('加载个人考试记录失败:', e)
      }
    },

    // 初始化图表
    initCharts() {
      this.initScoreChart()
      this.initTimeChart()
    },

    // 初始化分数段分布图表
    initScoreChart() {
      if (this.scoreChart) {
        this.scoreChart.dispose()
      }

      const chartDom = this.$refs.scoreChart
      if (!chartDom) return

      this.scoreChart = echarts.init(chartDom)

      const option = {
        title: {
          text: '分数段分布',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left'
        },
        series: [
          {
            name: '分数段',
            type: 'pie',
            radius: '50%',
            data: [
              { value: 0, name: '0-59分' },
              { value: 0, name: '60-69分' },
              { value: 0, name: '70-79分' },
              { value: 0, name: '80-89分' },
              { value: 0, name: '90-100分' }
            ],
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }
        ]
      }

      this.scoreChart.setOption(option)
    },

    // 初始化时间分布图表
    initTimeChart() {
      if (this.timeChart) {
        this.timeChart.dispose()
      }

      const chartDom = this.$refs.timeChart
      if (!chartDom) return

      this.timeChart = echarts.init(chartDom)

      const option = {
        title: {
          text: '时间分布',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        xAxis: {
          type: 'category',
          data: ['0-30分钟', '30-60分钟', '60-90分钟', '90-120分钟', '120分钟以上']
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            name: '学生人数',
            type: 'bar',
            data: [0, 0, 0, 0, 0]
          }
        ]
      }

      this.timeChart.setOption(option)
    },

    // 查看题目详情
    viewQuestionDetail(question) {
      this.selectedQuestion = question
      this.questionDetailVisible = true
    },

    // 获取进度条颜色
    getProgressColor(percentage) {
      if (percentage >= 80) return '#67C23A'
      if (percentage >= 60) return '#E6A23C'
      return '#F56C6C'
    },

    // 获取难度标签类型
    getDifficultyType(level) {
      switch (level) {
        case 1: return 'success'
        case 2: return 'warning'
        case 3: return 'danger'
        default: return 'info'
      }
    },

    // 格式化用时（秒=>分钟文案）
    formatUserTime(seconds) {
      if (seconds == null) return '—'
      const mins = Math.ceil(Number(seconds) / 60)
      return `${mins} 分钟`
    },

    // 刷新数据
    async refreshData() {
      if (this.selectedExamId) {
        await this.loadAnalysisData(this.selectedExamId)
      }
    },

    // 导出报告
    async exportReport() {
      if (!this.selectedExamId) {
        this.$message.warning('请先选择考试')
        return
      }

      this.exportLoading = true
      try {
        const response = await exportAnalysisReport(this.selectedExamId)
        // 修复：后端返回code为1表示成功，code为0表示失败
        if (response.code === 1) {
          this.$message.success('报告导出成功')
        } else {
          this.$message.error(response.msg || '报告导出失败')
        }
      } catch (error) {
        console.error('导出报告失败:', error)
        this.$message.error('导出报告失败')
      } finally {
        this.exportLoading = false
      }
    }
  },

  beforeDestroy() {
    if (this.scoreChart) {
      this.scoreChart.dispose()
    }
    if (this.timeChart) {
      this.timeChart.dispose()
    }
  }
}
</script>

<style scoped>
.exam-analysis-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.exam-selector {
  margin-bottom: 20px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.analysis-content {
  margin-top: 20px;
}

.exam-info-card {
  margin-bottom: 20px;
}

.info-item {
  text-align: center;
  padding: 10px;
}

.info-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 5px;
}

.info-value {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.question-content {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.answer-content {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-item {
  margin-bottom: 15px;
}

.detail-item label {
  font-weight: bold;
  color: #606266;
  margin-right: 10px;
}

.detail-item div {
  margin-top: 5px;
  color: #303133;
  line-height: 1.5;
}
</style>
